import i18n from 'i18n';
import Progress from 'components/common/Progress/Progress';
import { ColumnDescription } from 'components/common/Table/types';
import {
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import {
  DeviceAvailabilityRecord,
  DeviceUnitLevel,
  DeviceUnitRecord
} from 'core/deviceManagement/DeviceManagement';
import {
  detailColumn,
  healthStateColumn,
  storeNumColumn,
  unitNameColumn
} from './deviceListColumns';
import { formatFootTraffic } from './deviceDetail';
import {
  formatAvailabilityRate,
  formatSeconds,
  hasResolvedBusinessHours
} from './availabilityFormat';
import styles from './deviceManagement.module.scss';

export enum AvailabilityColumn {
  DAILY_AVERAGE_FOOTFALL = 'dailyAverageFootfall',
  AVAILABILITY_RATE = 'availabilityRate',
  AVAILABLE_SECONDS = 'availableSeconds',
  SOLD_SECONDS = 'soldSeconds'
}

const insufficientData = (testId: string) => (
  <span className={styles.insufficientData} data-testid={testId}>
    {i18n.t('deviceManagement.messages.insufficientBusinessHours')}
  </span>
);

const formatSecondsCell = (
  cell: number | null,
  record: DeviceAvailabilityRecord,
  testId: string,
  className?: string
) => {
  // 沒有營業時段資料時秒數是 null，不是 0：折成 0 會變成「這裡一秒都賣不掉」，
  // 而實際上是我們算不出來。整週不營業的點位才真的是 0，那一種照數字顯示。
  if (!hasResolvedBusinessHours(record.businessHoursProvenance)) {
    return insufficientData(testId);
  }
  return (
    <span className={className} data-testid={testId}>
      {formatSeconds(cell as number)}
    </span>
  );
};

const availabilityRateFormatter = (
  cell: number | null,
  record: DeviceAvailabilityRecord
) => {
  if (!hasResolvedBusinessHours(record.businessHoursProvenance)) {
    return insufficientData('availability-rate');
  }
  // 狀態不正常的機台賣不出去，後端就不給可售的數字。這裡連進度條都不要畫 ——
  // 一條長度不明的空槽比一個數字更容易被讀成「可售率 0%」，而那是另一回事。
  // 與上面那一種空白的差別在於原因：那是算不出來，這是算得出來但不該拿來賣。
  if (cell === null || cell === undefined) {
    return (
      <span data-testid='availability-rate'>
        {formatAvailabilityRate(cell)}
      </span>
    );
  }
  const rate = cell as number;
  return (
    <div
      className={styles.availabilityRateCell}
      data-testid='availability-rate'
    >
      {/* 共用的 Progress 吃的是 0 到 1，後端給的就是 0 到 1，不要先乘 100。 */}
      <Progress progress={rate} />
      <span>{formatAvailabilityRate(rate)}</span>
    </div>
  );
};

/**
 * 被包走的點位，名稱後面掛一個縮小的型態標注（包台 / 包版）。
 *
 * 只掛在可售狀況這一頁：標注問的是「走期內有沒有被包走」，而走期只有這一頁選得到。
 *
 * 文字走 PMP 既有的那一組 key，不另立一份：同一個東西在兩個地方叫不同名字，
 * 使用者對不起來，而兩份文案也會各自漂移。後端回的是型態清單，同一個點位同時被
 * 包台與包版佔到在實務上少見但資料做得出來，所以照著畫幾個。
 */
const PMP_TYPE_I18N: Record<string, string> = {
  BUYOUT: 'pmp.pmpTypes.buyout',
  SPONSORSHIP: 'pmp.pmpTypes.sponsorship'
};

const pmpBadge = (record: DeviceUnitRecord) => {
  const types = (record as DeviceAvailabilityRecord).pmpTypes ?? [];
  const known = types.filter((type) => PMP_TYPE_I18N[type]);
  if (known.length === 0) {
    return undefined;
  }
  return (
    <>
      {known.map((type) => (
        <span key={type} className={styles.pmpBadge} data-testid='pmp-badge'>
          {i18n.t(PMP_TYPE_I18N[type])}
        </span>
      ))}
    </>
  );
};

/**
 * 機台可售狀況的欄位。
 *
 * 只留識別用的三欄（名稱、店號、狀態）再接可售統計：這一頁要比的是可售狀況，
 * 地址與最後回報留在機台清單檢視那一頁。識別那三欄與清單共用同一組欄位函式，
 * 不是各寫一份，否則清單改了欄位這邊不會跟著動。
 */
export function buildAvailabilityColumns(
  unitLevel: DeviceUnitLevel,
  onShowDetail: (record: DeviceUnitRecord) => void
): ColumnDescription[] {
  return [
    unitNameColumn(unitLevel, onShowDetail, pmpBadge),
    storeNumColumn(),
    healthStateColumn(),
    renderColumn(
      sortableColumn(
        AvailabilityColumn.DAILY_AVERAGE_FOOTFALL,
        i18n.t('deviceManagement.headers.dailyAverageFootfall')
      ),
      (cell: number | null) => (
        <span data-testid='daily-average-footfall'>
          {formatFootTraffic(cell)}
        </span>
      )
    ),
    renderColumn(
      sortableColumn(
        AvailabilityColumn.AVAILABILITY_RATE,
        i18n.t('deviceManagement.headers.availabilityRate')
      ),
      availabilityRateFormatter
    ),
    renderColumn(
      sortableColumn(
        AvailabilityColumn.AVAILABLE_SECONDS,
        i18n.t('deviceManagement.headers.availableSeconds')
      ),
      (cell: number | null, record: DeviceAvailabilityRecord) =>
        formatSecondsCell(cell, record, 'available-seconds')
    ),
    renderColumn(
      sortableColumn(
        AvailabilityColumn.SOLD_SECONDS,
        i18n.t('deviceManagement.headers.soldSeconds')
      ),
      // 已售秒數在彙總卡與表格都是同一個顏色，共用 .soldValue 那一條。
      (cell: number | null, record: DeviceAvailabilityRecord) =>
        formatSecondsCell(cell, record, 'sold-seconds', styles.soldValue)
    ),
    detailColumn(onShowDetail)
  ];
}
